import Swal from "sweetalert2";

export const DRIVER_REJECTION_FIELDS = [
  { field: "licenseImageFront", label: "License Front Image", group: "Documents" },
  { field: "licenseImageBack", label: "License Back Image", group: "Documents" },
  { field: "licenseExpiryDate", label: "License Expiry Date", group: "Documents" },
  { field: "insurance", label: "Insurance Card", group: "Documents" },
  { field: "dob", label: "Date of Birth", group: "Personal Info" },
  { field: "ssn", label: "SSN", group: "Personal Info" },
  { field: "driverLicenseState", label: "License State", group: "Personal Info" },
  { field: "driverLicenseNumber", label: "License Number", group: "Personal Info" },
  { field: "firstName", label: "Full Name", group: "Personal Info" },
  { field: "vehicleName", label: "Vehicle Name", group: "Vehicle" },
  { field: "vehicleType", label: "Vehicle Type", group: "Vehicle" },
  { field: "brandName", label: "Brand Name", group: "Vehicle" },
  { field: "year", label: "Model Year", group: "Vehicle" },
  { field: "color", label: "Vehicle Color", group: "Vehicle" },
  { field: "numberPlate", label: "Plate Number", group: "Vehicle" },
  { field: "vehicleImage", label: "Vehicle Photos", group: "Vehicle" },
  { field: "insuranceExpiryDate", label: "Insurance Expiry Date", group: "Vehicle" },
  { field: "registrationExpiryDate", label: "Registration Expiry Date", group: "Vehicle" },
];

const GROUPS = ["Documents", "Personal Info", "Vehicle"];

/** Expiry fields whose date is missing or already past, for pre-ticking in the modal. */
export const getExpiryFieldsToFix = (dates = {}) =>
  Object.entries(dates)
    .filter(([, value]) => {
      const date = value ? new Date(value) : null;
      return !date || Number.isNaN(date.getTime()) || date.getTime() <= Date.now();
    })
    .map(([field]) => field);

const FIELD_HINTS = {
  licenseImageFront: "e.g. A bit blurry — please upload a clearer photo",
  licenseImageBack: "e.g. Details hard to read",
  licenseExpiryDate: "e.g. License has expired — please enter the new expiry date",
  insuranceExpiryDate: "e.g. Insurance has expired — please renew and update the date",
  registrationExpiryDate: "e.g. Registration has expired — please renew and update the date",
  insurance: "e.g. Looks expired — please upload a valid card",
  dob: "e.g. Doesn’t match the license",
  ssn: "e.g. Please double-check the number",
  driverLicenseState: "e.g. Wrong state selected",
  driverLicenseNumber: "e.g. Number unclear or incorrect",
  firstName: "e.g. Please match the name on ID",
  vehicleName: "e.g. Please correct the vehicle name",
  vehicleType: "e.g. Please pick the right vehicle type",
  brandName: "e.g. Brand doesn’t match photos",
  year: "e.g. Year looks incorrect",
  color: "e.g. Color doesn’t match photos",
  numberPlate: "e.g. Plate not clearly visible",
  vehicleImage: "e.g. Need clearer photos of all sides",
};

/**
 * Admin reject / request-resubmit modal: friendly checklist + notes for driver.
 *
 * @param {(payload: { reason: string, rejectedFields: Array<{field:string,label:string,reason:string}> }) => void} onConfirm
 * @param {{
 *   title?: string,
 *   confirmButtonText?: string,
 *   mode?: 'reject'|'resubmit',
 *   preselectedFields?: string[],
 *   groups?: string[],
 * }} [options]
 */
export const RejectDriverSignup = (onConfirm, options = {}) => {
  const {
    mode = "reject",
    preselectedFields = [],
    groups: groupsOption,
  } = options;

  const isVehicleOnly =
    Array.isArray(groupsOption) &&
    groupsOption.length === 1 &&
    groupsOption[0] === "Vehicle";

  const title =
    options.title ||
    (mode === "resubmit"
      ? isVehicleOnly
        ? "Ask for vehicle updates"
        : "Ask for document updates"
      : "Help driver update details");

  const confirmButtonText =
    options.confirmButtonText ||
    (mode === "resubmit" ? "Send request" : "Send feedback");

  const activeGroups =
    Array.isArray(groupsOption) && groupsOption.length > 0
      ? GROUPS.filter((g) => groupsOption.includes(g))
      : GROUPS;

  const preselected = new Set(
    (Array.isArray(preselectedFields) ? preselectedFields : []).map(String)
  );

  const tipTitle =
    mode === "resubmit"
      ? isVehicleOnly
        ? "Quick tip"
        : "Quick tip"
      : "Quick tip";

  const tipBody =
    mode === "resubmit"
      ? isVehicleOnly
        ? "Select only the vehicle items that need a refresh, add a short note if helpful, then send. The driver can update and come back for review."
        : "Select the items that need an update, add a short note if helpful, then send. The driver will fix them and re-submit for approval."
      : "Select what needs attention and leave a short, clear note. The driver will see this on their re-submit screen and can fix it easily.";

  const stepsHtml = `
    <div class="reject-steps">
      <div class="reject-steps-icon" aria-hidden="true">💡</div>
      <div class="reject-steps-body">
        <strong>${tipTitle}</strong>
        <p>${tipBody}</p>
      </div>
    </div>`;

  const groupsHtml = activeGroups
    .map((group) => {
      const items = DRIVER_REJECTION_FIELDS.filter((f) => f.group === group)
        .map(
          (item) => `
        <div class="reject-item" data-field-wrap="${item.field}">
          <label class="reject-item-main">
            <input
              type="checkbox"
              class="reject-field-cb"
              data-field="${item.field}"
              data-label="${item.label}"
              ${preselected.has(item.field) ? "checked" : ""}
            />
            <span class="reject-item-check" aria-hidden="true"></span>
            <span class="reject-item-label">${item.label}</span>
          </label>
          <div class="reject-item-note-wrap" hidden>
            <input
              type="text"
              class="reject-field-reason"
              data-field="${item.field}"
              placeholder="${FIELD_HINTS[item.field] || "Short note for the driver (optional)"}"
            />
          </div>
        </div>
      `
        )
        .join("");

      return `
      <div class="reject-group">
        <div class="reject-group-title">${group}</div>
        <div class="reject-group-list">${items}</div>
      </div>
    `;
    })
    .join("");

  const sectionLabel = isVehicleOnly
    ? "Which vehicle details need an update?"
    : "Which details need an update?";

  const messageLabel =
    mode === "resubmit" ? "Message to driver" : "Friendly note to driver";

  return Swal.fire({
    title,
    html: `
      <style>
        .reject-wrap { text-align: left; color: #334155; font-family: inherit; }
        .reject-steps {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
          border: 1px solid #e0f2fe;
          border-radius: 14px;
          padding: 14px 16px;
          margin-bottom: 16px;
        }
        .reject-steps-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #fff;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex: 0 0 36px;
        }
        .reject-steps-body strong {
          display: block;
          color: #0f172a;
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .reject-steps-body p {
          margin: 0;
          font-size: 13px;
          line-height: 1.5;
          color: #64748b;
        }
        .reject-label {
          display: block;
          font-weight: 700;
          font-size: 13px;
          margin-bottom: 4px;
          color: #0f172a;
        }
        .reject-help {
          font-size: 12px;
          color: #94a3b8;
          margin-bottom: 8px;
          line-height: 1.4;
        }
        #reject-general-reason {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 14px;
          resize: vertical;
          min-height: 72px;
          background: #fff;
          color: #0f172a;
          transition: border-color .15s, box-shadow .15s;
        }
        #reject-general-reason:focus {
          outline: none;
          border-color: #fb923c;
          box-shadow: 0 0 0 4px rgba(251, 146, 60, 0.15);
        }
        .reject-count {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #f1f5f9;
          color: #64748b;
          border-radius: 999px;
          padding: 5px 12px;
          font-size: 12px;
          font-weight: 600;
          margin: 14px 0 10px;
        }
        .reject-count.is-active {
          background: #fff7ed;
          color: #c2410c;
        }
        .reject-scroll {
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 12px;
          background: #f8fafc;
        }
        .reject-group { margin-bottom: 14px; }
        .reject-group:last-child { margin-bottom: 0; }
        .reject-group-title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #94a3b8;
          margin: 2px 0 8px;
        }
        .reject-item {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 8px;
          transition: border-color .15s, box-shadow .15s, background .15s, transform .15s;
        }
        .reject-item:hover {
          border-color: #fdba74;
        }
        .reject-item.is-selected {
          border-color: #fb923c;
          background: #fffbeb;
          box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.12);
        }
        .reject-item-main {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          margin: 0;
          user-select: none;
        }
        .reject-field-cb {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .reject-item-check {
          width: 22px;
          height: 22px;
          border: 2px solid #cbd5e1;
          border-radius: 7px;
          flex: 0 0 22px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          transition: all .15s;
        }
        .reject-item.is-selected .reject-item-check {
          border-color: #f97316;
          background: #f97316;
        }
        .reject-item.is-selected .reject-item-check::after {
          content: "";
          width: 5px;
          height: 10px;
          border: solid #fff;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
          margin-top: -2px;
        }
        .reject-item-label {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
        }
        .reject-item-note-wrap {
          margin-top: 10px;
          padding-left: 34px;
        }
        .reject-field-reason {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 9px 12px;
          font-size: 13px;
          background: #fff;
          color: #334155;
        }
        .reject-field-reason:focus {
          outline: none;
          border-color: #fb923c;
          box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.12);
        }
        .reject-signup-swal .swal2-title {
          font-size: 1.35rem !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          padding-top: 8px !important;
        }
        .reject-signup-swal .swal2-actions {
          gap: 10px;
          margin-top: 8px !important;
        }
        @media (max-width: 575.98px) {
          .reject-steps { padding: 12px; gap: 10px; }
          .reject-steps-icon { width: 30px; height: 30px; flex-basis: 30px; font-size: 15px; }
          .reject-scroll { padding: 8px; }
          .reject-item { padding: 10px 12px; }
          .reject-item-note-wrap { padding-left: 0; }
          .reject-signup-swal .swal2-title { font-size: 1.15rem !important; }
        }
      </style>
      <div class="reject-wrap">
        ${stepsHtml}

        <label class="reject-label" for="reject-general-reason">${messageLabel}</label>
        <div class="reject-help">Optional — a kind overall note helps the driver understand faster.</div>
        <textarea id="reject-general-reason" placeholder="Example: Almost there — please update the highlighted items and re-submit."></textarea>

        <div class="reject-count" id="reject-selected-count">0 selected</div>
        <div class="reject-label">${sectionLabel}</div>
        <div class="reject-help">Tap the items below. The driver will only need to fix what you select.</div>
        <div class="reject-scroll">${groupsHtml}</div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "Not now",
    width: 680,
    background: "#fff",
    color: "#0f172a",
    focusConfirm: false,
    heightAuto: false,
    scrollbarPadding: false,
    customClass: {
      confirmButton: "general-btn me-md-3 mb-2",
      cancelButton: "general-btn-3 mb-2",
      title: "modal-text mt-1 fw-regular",
      popup: "reject-signup-swal admin-swal",
      htmlContainer: "reject-signup-html",
      actions: "reject-signup-actions",
    },
    didOpen: () => {
      const popup = Swal.getPopup();
      const htmlContainer = Swal.getHtmlContainer();
      const countEl = popup.querySelector("#reject-selected-count");

      if (popup) {
        popup.style.maxHeight = "90vh";
        popup.style.display = "flex";
        popup.style.flexDirection = "column";
        popup.style.overflow = "hidden";
        popup.style.paddingBottom = "16px";
        popup.style.borderRadius = "18px";
      }
      if (htmlContainer) {
        htmlContainer.style.overflowY = "auto";
        htmlContainer.style.overflowX = "hidden";
        htmlContainer.style.maxHeight = "none";
        htmlContainer.style.flex = "1 1 auto";
        htmlContainer.style.overscrollBehavior = "contain";
        htmlContainer.style.webkitOverflowScrolling = "touch";
        htmlContainer.style.margin = "0";
        htmlContainer.style.padding = "0 10px 8px";

        htmlContainer.addEventListener(
          "wheel",
          (e) => {
            const el = htmlContainer;
            const atTop = el.scrollTop <= 0;
            const atBottom =
              Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
            if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
              e.preventDefault();
            }
            e.stopPropagation();
          },
          { passive: false }
        );
      }

      const refreshCount = () => {
        const n = popup.querySelectorAll(".reject-field-cb:checked").length;
        countEl.textContent = `${n} selected`;
        countEl.classList.toggle("is-active", n > 0);
      };

      const syncItem = (cb) => {
        const field = cb.getAttribute("data-field");
        const wrap = popup.querySelector(`[data-field-wrap="${field}"]`);
        const noteWrap = wrap?.querySelector(".reject-item-note-wrap");
        const reasonInput = wrap?.querySelector(".reject-field-reason");
        const checked = cb.checked;
        wrap?.classList.toggle("is-selected", checked);
        if (noteWrap) noteWrap.hidden = !checked;
        if (reasonInput && !checked) reasonInput.value = "";
      };

      popup.querySelectorAll(".reject-field-cb").forEach((cb) => {
        syncItem(cb);
        cb.addEventListener("change", () => {
          syncItem(cb);
          refreshCount();
        });
      });

      popup.querySelectorAll(".reject-item").forEach((item) => {
        item.addEventListener("click", (e) => {
          if (e.target.closest(".reject-field-reason")) return;
          const cb = item.querySelector(".reject-field-cb");
          if (!cb) return;
          if (e.target === cb || e.target.closest("label")) return;
          cb.checked = !cb.checked;
          cb.dispatchEvent(new Event("change"));
        });
      });

      refreshCount();
      if (htmlContainer) htmlContainer.scrollTop = 0;
    },
    preConfirm: () => {
      const popup = Swal.getPopup();
      const general = (
        popup.querySelector("#reject-general-reason")?.value || ""
      ).trim();
      const rejectedFields = [];
      popup.querySelectorAll(".reject-field-cb:checked").forEach((cb) => {
        const field = cb.getAttribute("data-field");
        const label = cb.getAttribute("data-label");
        const reason = (
          popup.querySelector(`.reject-field-reason[data-field="${field}"]`)
            ?.value || ""
        ).trim();
        rejectedFields.push({ field, label, reason });
      });

      if (!general && rejectedFields.length === 0) {
        Swal.showValidationMessage(
          "Select at least one item, or write a short note for the driver."
        );
        return false;
      }

      return { reason: general, rejectedFields };
    },
  }).then((result) => {
    if (result.isConfirmed && result.value) {
      onConfirm(result.value);
    }
  });
};
